import React, { useState, useRef, useEffect } from 'react';
import { RiArrowDownSLine, RiCheckLine, RiSearchLine } from 'react-icons/ri';
import styles from './Select.module.css';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  /** Label text displayed above the select */
  label?: string;
  /** Current selected value */
  value: string;
  /** Callback function when selection changes */
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Array of options to display */
  options: SelectOption[];
  /** Validation error message */
  error?: string;
  /** Disable select interaction */
  disabled?: boolean;
  /** Mark field as required with asterisk */
  required?: boolean;
  /** Placeholder text when no option selected */
  placeholder?: string;
  /** Size preset - matches Input component */
  size?: 'sm' | 'md' | 'lg';
  /** Border radius preset - matches Input component */
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  /** Whether the select should span full container width */
  fullWidth?: boolean;
  /** When true, the trigger becomes an editable input (combobox). Mobile always uses native select. */
  allowInput?: boolean;
  /** Controlled text value for the combobox input (only used when allowInput is true) */
  inputValue?: string;
  /** Called when the user types in the combobox input (only used when allowInput is true) */
  onInputChange?: (value: string) => void;
  /** When true, adds a search input inside the dropdown to filter options */
  searchable?: boolean;
}

export function Select({
  label,
  value,
  onChange,
  options,
  error,
  disabled = false,
  required = false,
  placeholder = 'Select an option...',
  size = 'md',
  radius = 'md',
  fullWidth = true,
  allowInput = false,
  inputValue,
  onInputChange,
  searchable = false,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const comboInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.innerWidth <= 768 ||
          /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      );
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCustomSelect = (optionValue: string) => {
    const syntheticEvent = {
      target: { value: optionValue },
      currentTarget: { value: optionValue },
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange(syntheticEvent);
    setIsOpen(false);
    setSearchQuery('');
    // Clear the combobox input text after selection
    if (allowInput && onInputChange) {
      onInputChange('');
    }
  };

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    } else if (searchable) {
      // Auto-focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, searchable]);

  const selectedOption = options.find((option) => option.value === value);

  // Compute filtered options for combobox mode or searchable mode
  const currentInputValue = inputValue ?? '';
  const searchFilter = searchable ? searchQuery.trim().toLowerCase() : '';
  const filteredOptions =
    allowInput && currentInputValue.trim() !== ''
      ? options.filter((opt) => opt.label.toLowerCase().includes(currentInputValue.toLowerCase()))
      : searchFilter
        ? options.filter(
            (opt) =>
              opt.label.toLowerCase().includes(searchFilter) ||
              opt.value.toLowerCase().includes(searchFilter),
          )
        : options;

  // Size and radius classes for consistent styling
  const sizeClass = styles[`size${size.charAt(0).toUpperCase() + size.slice(1)}`];
  const radiusClass = styles[`radius${radius.charAt(0).toUpperCase() + radius.slice(1)}`];

  // Use native select on mobile (allowInput is ignored on mobile for backwards compat)
  if (isMobile) {
    return (
      <div className={`${styles.container} ${fullWidth ? styles.fullWidth : ''}`}>
        {label && (
          <label className={styles.label}>
            {label}
            {required && <span className={styles.requiredMark}>*</span>}
          </label>
        )}
        <select
          ref={selectRef}
          className={`${styles.input} ${styles.native} ${sizeClass} ${radiusClass} ${error ? styles.hasError : ''}`}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <span className={styles.errorMsg}>{error}</span>}
      </div>
    );
  }

  // Custom dropdown for desktop
  return (
    <div className={`${styles.container} ${fullWidth ? styles.fullWidth : ''}`}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.requiredMark}>*</span>}
        </label>
      )}
      <div
        ref={dropdownRef}
        className={`${styles.customSelect} ${isOpen ? styles.open : ''} ${error ? styles.hasError : ''} ${disabled ? styles.disabled : ''}`}
      >
        <div
          className={`${styles.trigger} ${sizeClass} ${radiusClass}`}
          onClick={() => {
            if (disabled) return;
            if (!allowInput) {
              setIsOpen(!isOpen);
            }
            // In combobox mode clicks on the trigger area (outside the input)
            // are handled by the input's own focus/click handlers
          }}
          onKeyDown={(e) => {
            if (allowInput) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!disabled) setIsOpen(!isOpen);
            }
          }}
          tabIndex={allowInput || disabled ? -1 : 0}
          role={allowInput ? undefined : 'button'}
          aria-haspopup={allowInput ? undefined : 'listbox'}
          aria-expanded={allowInput ? undefined : isOpen}
        >
          {allowInput ? (
            <input
              ref={comboInputRef}
              type="text"
              className={`${styles.comboInput} ${selectedOption ? styles.comboInputHasValue : ''}`}
              value={currentInputValue}
              placeholder={selectedOption ? selectedOption.label : placeholder}
              disabled={disabled}
              aria-autocomplete="list"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              role="combobox"
              onChange={(e) => {
                onInputChange?.(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => {
                if (!disabled) setIsOpen(true);
              }}
              onClick={(e) => {
                // Prevent the parent trigger div from toggling the dropdown closed
                e.stopPropagation();
                if (!disabled && !isOpen) setIsOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsOpen(false);
                  comboInputRef.current?.blur();
                }
                if (e.key === 'Enter' && filteredOptions.length === 1) {
                  e.preventDefault();
                  handleCustomSelect(filteredOptions[0].value);
                }
              }}
            />
          ) : (
            <span className={styles.value}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          )}
          <RiArrowDownSLine
            className={`${styles.arrow} ${isOpen ? styles.rotated : ''}`}
            onClick={(e) => {
              if (!allowInput) return;
              e.stopPropagation();
              if (!disabled) {
                setIsOpen(!isOpen);
                if (!isOpen) comboInputRef.current?.focus();
              }
            }}
          />
        </div>

        {isOpen && (
          <div className={styles.dropdown}>
            {searchable && (
              <div className={styles.searchBox}>
                <RiSearchLine className={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsOpen(false);
                    }
                    if (e.key === 'Enter' && filteredOptions.length === 1) {
                      e.preventDefault();
                      handleCustomSelect(filteredOptions[0].value);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className={styles.options} role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`${styles.option} ${value === option.value ? styles.selected : ''}`}
                    onClick={() => handleCustomSelect(option.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCustomSelect(option.value);
                      }
                    }}
                    tabIndex={0}
                    role="option"
                    aria-selected={value === option.value}
                  >
                    <span className={styles.optionText}>{option.label}</span>
                    {value === option.value && <RiCheckLine className={styles.optionCheck} />}
                  </div>
                ))
              ) : (
                <div className={styles.noResults}>No results</div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <span className={styles.errorMsg}>{error}</span>}
    </div>
  );
}
